// @ts-nocheck
import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import {
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
  TrashIcon,
  UserPlusIcon,
  UsersIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import Header from '../../components/TitleAndSubtitle';
import Breadcrumb from '../../components/Breadcrumb';
import { URL } from '../../service/Service';
import { useI18n } from '../../i18n/I18nContext';

const headers = () => ({
  Accept: 'application/json',
  'Content-Type': 'application/json',
  Authorization: `Bearer ${sessionStorage.getItem('jwt') || ''}`,
});

export default function CompanyUsers() {
  const { t, language } = useI18n();
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ userEmail: '' });
  const [notice, setNotice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [userToRemove, setUserToRemove] = useState(null);
  const [removing, setRemoving] = useState(false);
  const [removeError, setRemoveError] = useState('');

  async function loadUsers() {
    setLoading(true);
    try {
      const response = await fetch(`${URL}/api/users/company`, { headers: headers() });
      if (!response.ok) throw new Error(await response.text());
      const data = await response.json();
      setUsers(Array.isArray(data) ? data : []);
    } catch {
      setNotice({ type: 'error', message: 'Não foi possível carregar a equipa.' });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadUsers(); }, []);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return users.filter((user) => !query || String(user.userEmail || '').toLowerCase().includes(query));
  }, [users, search]);

  async function createUser(event) {
    event.preventDefault();
    setNotice(null);
    setSubmitting(true);
    try {
      const response = await fetch(`${URL}/api/users/createuser`, {
        method: 'POST',
        headers: { ...headers(), 'Accept-Language': language.toLowerCase() },
        body: JSON.stringify({ userEmail: form.userEmail }),
      });
      if (!response.ok) throw new Error(await response.text());
      setForm({ userEmail: '' });
      setShowCreate(false);
      setNotice({ type: 'success', message: 'Conta criada. O convite para definir a palavra-passe será enviado por email.' });
      await loadUsers();
    } catch (error) {
      setNotice({ type: 'error', message: error.message || 'Não foi possível criar o acesso.' });
    } finally {
      setSubmitting(false);
    }
  }

  async function removeUser(user) {
    if (removing) return;
    setRemoving(true);
    setRemoveError('');
    try {
      const response = await fetch(`${URL}/api/users/${user.id}`, { method: 'DELETE', headers: headers() });
      if (!response.ok) throw new Error(await response.text());
      setNotice({ type: 'success', message: 'Acesso removido.' });
      setUsers((current) => current.filter((item) => item.id !== user.id));
      setUserToRemove(null);
    } catch {
      setRemoveError('Não foi possível remover este acesso.');
    } finally {
      setRemoving(false);
    }
  }

  return (
    <main className="min-h-[calc(100vh-5rem)] bg-gradient-to-br from-slate-50 via-white to-emerald-50/50 px-4 py-7 sm:px-8 lg:px-12">
      <div className="mx-auto max-w-7xl">
        <Breadcrumb pages={[
          { name: 'Dashboard', href: '/company/dashboard', current: false },
          { name: 'Equipa', href: '/company/users', current: true },
        ]} />
        <div className="mt-6">
          <Header
            eyebrow="Empresa"
            title="Equipa"
            description="Faça a gestão dos utilizadores associados à sua empresa."
            actions={(
              <button type="button" onClick={() => setShowCreate(true)} className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-green-cl px-5 py-2.5 text-sm font-bold text-white shadow-sm">
                <UserPlusIcon className="h-5 w-5" /> Novo utilizador
              </button>
            )}
          />
        </div>

        {notice && (
          <div className={`mb-5 rounded-2xl border p-4 text-sm font-medium ${notice.type === 'error' ? 'border-red-200 bg-red-50 text-red-700' : 'border-emerald-200 bg-emerald-50 text-emerald-700'}`}>
            {notice.message}
          </div>
        )}

        <section className="overflow-hidden rounded-3xl border border-slate-200/80 bg-white shadow-sm">
          <div className="flex flex-col gap-4 border-b border-slate-100 p-5 sm:flex-row sm:items-center sm:justify-between">
            <label className="relative block w-full max-w-xl">
              <MagnifyingGlassIcon className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
              <input type="search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Procurar por email" className="h-12 w-full rounded-xl border border-slate-200 bg-slate-50 pl-12 pr-4 text-sm outline-none focus:border-emerald-400 focus:bg-white focus:ring-4 focus:ring-emerald-100" />
            </label>
            <p className="text-sm text-slate-500"><strong className="text-slate-800">{filtered.length}</strong> utilizadores</p>
          </div>

          {loading ? (
            <div className="p-12 text-center text-sm text-slate-500">A carregar equipa…</div>
          ) : filtered.length ? (
            <div className="divide-y divide-slate-100">
              {filtered.map((user) => (
                <article key={user.id} className="flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:justify-between sm:px-6">
                  <div className="flex min-w-0 items-center gap-4">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-emerald-50 font-bold text-emerald-800">{String(user.userEmail || 'U')[0].toUpperCase()}</span>
                    <div className="min-w-0">
                      <p className="truncate font-bold text-slate-900">{user.userEmail}</p>
                      <p className="mt-1 text-xs font-semibold uppercase tracking-wider text-slate-400">Utilizador</p>
                    </div>
                  </div>
                  <button type="button" onClick={() => { setRemoveError(''); setUserToRemove(user); }} className="inline-flex min-h-10 items-center justify-center gap-2 rounded-xl border border-red-100 px-4 text-sm font-bold text-red-600 hover:bg-red-50">
                    <TrashIcon className="h-4 w-4" /> Remover acesso
                  </button>
                </article>
              ))}
            </div>
          ) : (
            <div className="p-12 text-center">
              <UsersIcon className="mx-auto h-10 w-10 text-slate-300" />
              <p className="mt-3 font-bold text-slate-700">Ainda não existem utilizadores.</p>
              <p className="mt-1 text-sm text-slate-500">Crie o primeiro acesso para a sua equipa.</p>
            </div>
          )}
        </section>
      </div>

      <Dialog
        open={Boolean(userToRemove)}
        onClose={() => { if (!removing) setUserToRemove(null); }}
        aria-labelledby="remove-user-title"
        aria-describedby="remove-user-description"
        fullWidth maxWidth="xs"
        PaperProps={{ sx: { borderRadius: '24px', p: 2 } }}
        BackdropProps={{ sx: { backdropFilter: 'blur(4px)', backgroundColor: 'rgba(15,23,42,0.45)' } }}
      >
        <DialogTitle id="remove-user-title" sx={{ fontWeight: 800, color: '#173c37' }}>
          <span className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-red-50 text-red-600"><TrashIcon className="h-6 w-6" /></span>
          {t('Remover acesso')}
        </DialogTitle>
        <DialogContent>
          <p id="remove-user-description" className="text-sm leading-6 text-slate-600">
            {t('Remover o acesso de')} <strong className="break-all text-slate-900">{userToRemove?.userEmail}</strong>?
          </p>
          {removeError && <p role="alert" className="mt-4 rounded-xl bg-red-50 p-3 text-sm text-red-700">{t(removeError)}</p>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button autoFocus disabled={removing} onClick={() => setUserToRemove(null)} variant="outlined" sx={{ borderRadius: '12px', textTransform: 'none', color: '#334155', borderColor: '#cbd5e1' }}>{t('Cancelar')}</Button>
          <Button disabled={removing} onClick={() => removeUser(userToRemove)} variant="contained" color="error" sx={{ borderRadius: '12px', textTransform: 'none', fontWeight: 700, boxShadow: 'none' }}>{removing ? t('A remover…') : t('Remover acesso')}</Button>
        </DialogActions>
      </Dialog>

      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 p-4 backdrop-blur-sm">
          <form onSubmit={createUser} className="w-full max-w-lg rounded-3xl bg-white p-6 shadow-2xl sm:p-8">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-700">Novo acesso</p>
                <h2 className="mt-2 text-2xl font-extrabold text-slate-950">Adicionar utilizador</h2>
              </div>
              <button type="button" onClick={() => setShowCreate(false)} className="rounded-xl p-2 text-slate-500 hover:bg-slate-100"><XMarkIcon className="h-5 w-5" /></button>
            </div>
            <div className="mt-6 space-y-4">
              <label className="block text-sm font-semibold text-slate-800">Email
                <input type="email" required value={form.userEmail} onChange={(event) => setForm({ ...form, userEmail: event.target.value })} className="mt-2 block h-12 w-full rounded-xl border border-slate-300 px-4 outline-none focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100" />
              </label>
              <p className="text-sm leading-6 text-slate-500">O novo utilizador receberá um email com um link para definir a sua palavra-passe. O link é válido durante 15 minutos. Se expirar, pode pedir outro em Recuperar palavra-passe.</p>
              <p className="flex gap-2 text-xs leading-5 text-slate-500"><ExclamationTriangleIcon className="h-4 w-4 shrink-0" /> Este acesso terá a função USER e ficará automaticamente associado à sua empresa.</p>
            </div>
            <div className="mt-7 flex justify-end gap-3 border-t border-slate-100 pt-5">
              <button type="button" onClick={() => setShowCreate(false)} className="rounded-xl border border-slate-200 px-4 py-3 text-sm font-bold text-slate-700">Cancelar</button>
              <button type="submit" disabled={submitting} className="rounded-xl bg-green-cl px-5 py-3 text-sm font-bold text-white disabled:opacity-50">{submitting ? 'A criar…' : 'Criar e enviar convite'}</button>
            </div>
          </form>
        </div>
      )}
    </main>
  );
}
